import React from "react";
import { Box, Stack } from "@mui/material";
import { ParlorDetailProps, ClickEventType } from "../../../types";
import { ColorSystemProps } from "../../../constants";
import { customEvent } from "../../../utils";

export function ETypeBinsoPriceSummary({
  defaultParlor,
  mainColor,
  ctaHref,
  ctaText,
  event,
}: {
  defaultParlor: ParlorDetailProps;
  mainColor: ColorSystemProps;
  ctaHref?: string;
  ctaText?: string;
  event?: ClickEventType;
}) {
  const binsos = [...(defaultParlor.binsos || [])].sort(
    (a, b) => (a.charge || 0) - (b.charge || 0),
  );
  const avg = defaultParlor.binso_average_charge;

  if (!binsos.length) return null;

  const minPy = Math.min(...binsos.map((b) => b.pyeong || 0));
  const maxPy = Math.max(...binsos.map((b) => b.pyeong || 0));

  return (
    <Stack gap="0" padding="56px 20px">
      <Box
        component="h2"
        className="h2"
        style={{
          fontSize: "24px",
          fontWeight: 700,
          marginTop: "14px",
          color: "#252525",
        }}
      >
        호실별 평형과 1일 사용료
      </Box>
      <Box
        component="p"
        style={{
          marginTop: "14px",
          fontSize: "16px",
          lineHeight: 1.75,
          color: "#535353",
          maxWidth: "720px",
        }}
      >
        총 {binsos.length}실({minPy}~{maxPy}평)을 운영합니다. 아래 금액은 빈소{" "}
        <strong>1일 사용료</strong>입니다. 보통 3일장을 치르시므로, 실제 부담 금액은 3배로 보시면 됩니다.
      </Box>

      <Stack
        component="ul"
        gap="10px"
        style={{ marginTop: "28px", listStyle: "none", padding: 0 }}
      >
        {binsos.map((b, i) => {
          const type = (b.item || "").includes("특") ? "특실" : "일반실";
          const num = String(i + 1).padStart(2, "0");
          return (
            <Box
              key={i}
              component="li"
              style={{
                display: "grid",
                gridTemplateColumns: "48px 1fr auto",
                gap: "20px",
                alignItems: "center",
                background: "#fff",
                border: "1px solid #e9e9e9",
                borderRadius: "12px",
                padding: "22px 24px",
              }}
            >
              <Box
                style={{
                  fontSize: "13px",
                  fontWeight: 800,
                  color: mainColor.c500,
                  letterSpacing: "0.1em",
                  fontFeatureSettings: '"tnum" on',
                  alignSelf: "flex-start",
                  paddingTop: "6px",
                }}
              >
                {num}
              </Box>
              <Box>
                <Box
                  style={{
                    fontSize: "18px",
                    fontWeight: 800,
                    color: "#252525",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {b.pyeong || 0}평
                </Box>
                <Box
                  style={{
                    marginTop: "6px",
                    fontSize: "14px",
                    color: "#535353",
                  }}
                >
                  {b.item || "—"}
                  <Box
                    component="small"
                    style={{
                      display: "inline-block",
                      marginLeft: "8px",
                      padding: "2px 8px",
                      borderRadius: "999px",
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#8f8f8f",
                      background: "#f6f4f4",
                    }}
                  >
                    {type}
                  </Box>
                </Box>
              </Box>
              <Box style={{ textAlign: "right" }}>
                <Box
                  style={{
                    fontSize: "18px",
                    fontWeight: 800,
                    color: "#252525",
                    fontFeatureSettings: '"tnum" on',
                    lineHeight: 1.2,
                  }}
                >
                  {(b.charge || 0).toLocaleString("ko-KR")}원
                </Box>
                <Box
                  component="small"
                  style={{
                    display: "block",
                    marginTop: "4px",
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#8f8f8f",
                  }}
                >
                  / 1일
                </Box>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {avg ? (
        <Box
          component="p"
          style={{
            marginTop: "16px",
            fontSize: "12px",
            color: "#8f8f8f",
            lineHeight: 1.5,
          }}
        >
          * 빈소 평균 사용료는{" "}
          <strong>{avg.toLocaleString("ko-KR")}원/1일</strong> 수준입니다. 실제
          부과 금액은 빈소 선택과 사용 일수에 따라 달라집니다.
        </Box>
      ) : null}

      {ctaHref && (
        <Box style={{ marginTop: "24px" }}>
          {ctaText && (
            <Box style={{ fontSize: "13px", color: "#8f8f8f", marginBottom: "10px", textAlign: "center", whiteSpace: "pre-wrap" }}>
              {ctaText}
            </Box>
          )}
          <Box
            component="a"
            href={ctaHref}
            onClick={event ? () => customEvent(event) : undefined}
            style={{
              display: "block",
              padding: "16px",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: 700,
              background: mainColor.c400,
              color: "#fff",
              textDecoration: "none",
              textAlign: "center",
            }}
          >
            장례 비용 확인하기
          </Box>
          <Box
            component="p"
            style={{ marginTop: "10px", fontSize: "12px", color: "#888", lineHeight: 1.5, textAlign: "center" }}
          >
            ※ e하늘장사정보시스템을 기반으로 고이장례연구소에서 예상 값을 산출하여 제공해요
          </Box>
        </Box>
      )}
    </Stack>
  );
}
